import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../../components/Shell";
import api from "../../lib/api";
import { useCart } from "../../context/CartContext";
import AuthImage from "../../components/AuthImage";
import { Search, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Catalogue() {
    const [products, setProducts] = useState([]);
    const [brands, setBrands] = useState([]);
    const [brandFilter, setBrandFilter] = useState("all");
    const [q, setQ] = useState("");
    const { add } = useCart();

    useEffect(() => {
        api.get("/brands").then(r => setBrands(r.data)).catch(() => {});
        api.get("/products").then(r => setProducts(r.data || [])).catch(() => {});
    }, []);

    const filtered = useMemo(() => {
        let list = products;
        if (brandFilter !== "all") list = list.filter(p => p.brand_id === brandFilter);
        if (q) {
            const qq = q.toLowerCase();
            list = list.filter(p => p.name.toLowerCase().includes(qq) || (p.sku || "").toLowerCase().includes(qq));
        }
        return list;
    }, [products, brandFilter, q]);

    return (
        <Shell variant="clinic">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 animate-fade-in">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-10 gap-4">
                    <div>
                        <p className="eyebrow mb-2">Professional catalogue</p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight">Products</h1>
                    </div>
                    <div className="relative w-full sm:w-72">
                        <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input data-testid="catalogue-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or SKU" className="input-base pl-9" />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-10 border-b border-zinc-200 pb-6" data-testid="brand-filters">
                    <BrandPill active={brandFilter === "all"} onClick={() => setBrandFilter("all")}>All</BrandPill>
                    {brands.map(b => (
                        <BrandPill key={b.id} active={brandFilter === b.id} onClick={() => setBrandFilter(b.id)}>{b.name}</BrandPill>
                    ))}
                </div>

                {filtered.length === 0 ? (
                    <div className="text-center py-20 border border-dashed border-zinc-200 rounded-sm">
                        <p className="eyebrow mb-2">No products yet</p>
                        <p className="text-sm text-zinc-500">The catalogue is being prepared. Please check back soon.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filtered.map(p => (
                            <article key={p.id} data-testid={`product-card-${p.id}`} className="card-base hover:border-zinc-400 transition-colors group">
                                <Link to={`/catalogue/${p.id}`} className="block aspect-square bg-zinc-50 overflow-hidden">
                                    <AuthImage path={p.image_path} alt={p.name} className="w-full h-full object-contain p-8 group-hover:scale-[1.02] transition-transform duration-300" />
                                </Link>
                                <div className="p-5">
                                    <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1">{brands.find(b => b.id === p.brand_id)?.name || ""}</p>
                                    <h3 className="font-heading text-base font-medium mb-1 leading-tight"><Link to={`/catalogue/${p.id}`} className="hover:underline">{p.name}</Link></h3>
                                    <p className="text-xs text-zinc-500 mb-4">SKU {p.sku}</p>
                                    <div className="flex items-center justify-between">
                                        <p className="font-heading text-lg">${(p.price || 0).toFixed(2)} <span className="text-xs text-zinc-400">{p.currency || "NZD"}</span></p>
                                        <button data-testid={`add-to-cart-${p.id}`} onClick={() => { add(p, 1); toast.success(`${p.name} added to cart`); }} className="btn-ghost"><Plus size={14} strokeWidth={1.5} /> Add</button>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </Shell>
    );
}

function BrandPill({ active, onClick, children }) {
    return (
        <button onClick={onClick} className={`text-xs uppercase tracking-widest px-3 py-2 border rounded-sm transition-colors ${active ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-200 hover:border-black"}`}>{children}</button>
    );
}
